import React, { useCallback, useEffect, useState } from 'react';
import { AuthPagesWrapper } from '@components/AuthPagesWrapper/AuthPagesWrapper';
import {
    Button,
    Divider,
    FormHelperText,
    GridLegacy as Grid,
    TextField,
    Typography,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { themeColors } from '@styles/theme';
import { useTranslation } from 'react-i18next';
import CheckEmailPage from './CheckEmailPage/CheckEmailPage';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import BackToSignInButton from '@components/BackToSignInButton/BackToSignInButton';
import { useAuth } from '@context/authContext';
import { useNavigate } from 'react-router-dom';
import { isErrorResponse } from '@utils/utils';
import { useToastContext } from '@context/toastContext';

type FormValuesType = {
    email: string;
};

export const ResetPassWordPage = () => {
    const [email, setEmail] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [requestedPasswordChange, setPasswordChangeRequest] = useState<boolean>(false);
    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<FormValuesType>();
    const { generateResetToken } = useAuth();
    const { token } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { addToast } = useToastContext();

    useEffect(() => {
        if (token) {
            navigate('/', { state: { from: location.pathname } });
        }
    }, [token]);

    const onSubmit: SubmitHandler<FormValuesType> = useCallback(
        async ({ email }, event) => {
            event?.preventDefault();

            const resp = await generateResetToken(email);
            if (!resp || isErrorResponse(resp)) {
                addToast(t('common.error-toast'), 'error');
                return;
            }

            setEmail(email);
            setLoading(true);

            setPasswordChangeRequest(true);
            setLoading(false);
        },
        [generateResetToken]
    );

    const validateEmail = useCallback(value => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        if (!value || !emailRegex.test(value)) {
            return t('reset-password-form.validation-errors.invalid-email');
        }
        return true;
    }, []);

    if (requestedPasswordChange) {
        return <CheckEmailPage email={email} resendFn={generateResetToken} />;
    }

    return (
        <LoadingBackdrop isVisible={loading} transparent>
            <AuthPagesWrapper>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={12}>
                        <Typography component="h1" variant="h5">
                            {t('reset-password-form.title')}
                        </Typography>
                        <Typography component="p">{t('reset-password-form.message')}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Controller
                                name="email"
                                control={control}
                                rules={{
                                    required: true,
                                    validate: validateEmail,
                                }}
                                render={({ field, fieldState, formState }) => (
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        label={t('reset-password-form.email-label')}
                                        autoFocus
                                        onChange={field.onChange}
                                        sx={{ background: themeColors.white }}
                                        error={!!errors.email}
                                    />
                                )}
                            />
                            {!!errors.email && (
                                <FormHelperText error id="email-error">
                                    {errors.email.message}
                                </FormHelperText>
                            )}
                            <Divider />
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ my: 2 }}
                            >
                                <BackToSignInButton
                                    title={t('reset-password-form.back-to-login-link')}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    // sx={{mt: 3, mb: 2}}
                                    style={{ background: themeColors.purple }}
                                >
                                    {t('reset-password-form.button')}
                                </Button>
                            </Grid>
                        </form>
                    </Grid>
                </Grid>
            </AuthPagesWrapper>
        </LoadingBackdrop>
    );
};
